import PropTypes from "prop-types";
import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { Skeleton } from "@/components/ui/skeleton";
import { BaseForm, FormCombobox } from "components/BaseForm";
import { DEFAULT_CAMPAIGN_ID } from "constants/Constants";
import { useCampaignOptions } from "hooks/useCampaignOptions";
import CallQueueService from "services/CallQueue";
import ContactService from "services/Contact";
import DialSessionService from "services/DialSessions";
import EmailUrlService from "services/EmailUrls";
import VoicemailService from "services/Voicemails";

const requiredId = message =>
  z.number({ required_error: message, invalid_type_error: message });

const toOptions = records =>
  records.map(({ id, name }) => ({ key: id, value: id, text: name }));

const callErrorMessage = error =>
  error.response?.data?.message ?? error.message;

const startPrivateCall = async ({
  contactId,
  campaignId,
  voicemailId,
  formId,
}) => {
  const callableInfo =
    await ContactService.checkContactCallableHours(contactId);
  const nonCallableMessage = `Contact is outside calling hours ( ${callableInfo.window_start}:00 - ${callableInfo.window_end}:00 ${callableInfo.time_zone} )`;
  if (!callableInfo.callable) {
    throw new Error(nonCallableMessage);
  }

  const dialSession = await DialSessionService.createPrivateSession({
    name: `Contact ${contactId} - ${new Date().toISOString()}`,
    campaign_id: campaignId,
    status: true,
    voicemail_id: voicemailId,
    form_id: formId,
    info: { enable_recording: true },
  });
  await CallQueueService.addContact(dialSession.id, contactId, true);
  const contactExists = await CallQueueService.nextContactExists(
    dialSession.id
  );
  if (!contactExists) {
    throw new Error(nonCallableMessage);
  }
  window.open(
    `/dialer/${dialSession.id}?private=1`,
    "_blank",
    "location=yes,height=750,width=1800,scrollbars=yes,status=yes"
  );
};

const CallForm = ({ contactId, campaignId, voicemails, forms, onStarted }) => {
  const isDefaultCampaign = campaignId === DEFAULT_CAMPAIGN_ID;

  const { campaigns, isLoading: isLoadingCampaigns } = useCampaignOptions({
    filters: { contact_id: contactId },
    enabled: isDefaultCampaign,
  });
  // Shares its key with the Contacts table, which preloads the same list.
  const { data: fetchedVoicemails = [], isLoading: isLoadingVoicemails } =
    useQuery({
      queryKey: ["voicemails"],
      queryFn: VoicemailService.getVoicemailsForFilter,
      enabled: !voicemails,
    });
  const { data: fetchedForms = [], isLoading: isLoadingForms } = useQuery({
    queryKey: ["email-url-forms"],
    queryFn: EmailUrlService.getForms,
    enabled: !isDefaultCampaign && !forms,
  });

  const call = useMutation({
    mutationFn: startPrivateCall,
    onSuccess: onStarted,
  });

  const schema = useMemo(
    () =>
      z.object({
        campaign: isDefaultCampaign
          ? requiredId("Campaign is required")
          : z.number().nullable(),
        voicemailId: requiredId("Voicemail is required"),
        formId: z.number().nullable(),
      }),
    [isDefaultCampaign]
  );
  const defaultValues = useMemo(
    () => ({ campaign: null, voicemailId: null, formId: null }),
    []
  );

  const campaignOptions = useMemo(() => toOptions(campaigns), [campaigns]);
  const voicemailOptions = useMemo(
    () => toOptions(voicemails ?? fetchedVoicemails),
    [voicemails, fetchedVoicemails]
  );
  const formOptions = useMemo(
    () => toOptions(forms ?? fetchedForms),
    [forms, fetchedForms]
  );

  // A failure stays in `call.error` and renders through `errorMessage`.
  // Rethrowing would send it through BaseForm's error path, which replaces any
  // error without a 4xx status, such as the calling-hours one, with a generic
  // fallback.
  const handleSubmit = ({ campaign, voicemailId, formId }) =>
    call
      .mutateAsync({
        contactId,
        campaignId: campaign ?? campaignId,
        voicemailId,
        formId,
      })
      .catch(() => {});

  if (isLoadingCampaigns || isLoadingVoicemails || isLoadingForms) {
    return (
      <div
        className="tw:flex tw:flex-col tw:gap-4"
        role="status"
        aria-label="Loading options"
      >
        <Skeleton className="tw:h-10 tw:w-full" />
        <Skeleton className="tw:h-10 tw:w-full" />
      </div>
    );
  }

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      shouldDisableSubmit={values =>
        values.voicemailId == null ||
        (isDefaultCampaign && values.campaign == null)
      }
      errorMessage={call.error ? callErrorMessage(call.error) : null}
      submitButtonText="Start Call"
      submittingButtonText="Starting Call"
    >
      {isDefaultCampaign && (
        <FormCombobox
          name="campaign"
          label="Campaign"
          required
          options={campaignOptions}
          placeholder="Select Campaign"
        />
      )}
      <FormCombobox
        name="voicemailId"
        label="Voicemail"
        required
        options={voicemailOptions}
        placeholder="Select Voicemail"
      />
      {!isDefaultCampaign && (
        <FormCombobox
          name="formId"
          label="Questionnaire"
          clearable
          options={formOptions}
          placeholder="Select Questionnaire"
        />
      )}
    </BaseForm>
  );
};

CallForm.propTypes = {
  contactId: PropTypes.number.isRequired,
  campaignId: PropTypes.number.isRequired,
  voicemails: PropTypes.array,
  forms: PropTypes.array,
  onStarted: PropTypes.func.isRequired,
};

export default CallForm;
